import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, Edit, Save, X, Loader2, Sparkles, FileText, Brain, Copy,
  Target, Users, ToggleLeft, ToggleRight, Send, Check, ExternalLink, RefreshCw,
  AlertTriangle, ChevronDown, ChevronUp, Plus, Pencil, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API } from '@/App';

import { HealthBadge } from '@/components/transcript/HealthBadge';
import { DiarizedTranscript } from '@/components/transcript/DiarizedTranscript';
import { DiagnosisCard } from '@/components/transcript/DiagnosisCard';
import { MedicalNoteCard } from '@/components/transcript/MedicalNoteCard';
import { PsychotherapyNoteCard } from '@/components/transcript/PsychotherapyNoteCard';
import { CustomTemplateModal } from '@/components/transcript/CustomTemplateModal';
import { EhrPushHistory } from '@/components/transcript/EhrPushHistory';

import { useTranscriptData } from '@/hooks/useTranscriptData';
import { useAiNotes } from '@/hooks/useAiNotes';
import { useEhrPush } from '@/hooks/useEhrPush';
import { useCustomTemplates, useCustomEhrs } from '@/hooks/useCustomTemplates';

// Universal copy-to-clipboard helper used by every card.
const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  } catch {
    toast.error('Failed to copy to clipboard');
  }
};

export default function TranscriptViewer({ user, token, onLogout }) {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  // ─── Hooks: state + behavior split into cohesive groups ────────────
  const td = useTranscriptData({ sessionId, token, navigate, user });
  const notes = useAiNotes({
    sessionId,
    token,
    transcript: td.transcript,
    sessionData: td.sessionData,
    setSessionData: td.setSessionData,
  });
  const customEhrs = useCustomEhrs(token);
  const ehr = useEhrPush({
    sessionId,
    token,
    user,
    transcript: td.transcript,
    sessionData: td.sessionData,
    medicalNote: notes.medicalNote,
    psychotherapyNote: notes.psychotherapyNote,
    diagnosis: notes.diagnosis,
    customEhrs,
  });
  const tpl = useCustomTemplates({
    token,
    selectedTemplateKey: ehr.tebraTemplateKey,
    onSelectTemplate: (key) => {
      ehr.setTebraTemplateKey(key);
      ehr.setTebraPreviewSections(null);
      ehr.setTebraExpanded({});
    },
    onTemplatesReloaded: ehr.reloadTemplates,
  });

  // Auto-show the diarized view when the transcript has it but no edits yet.
  if (td.transcript?.diarized_text && !td.transcript?.edited_text && !td.showDiarized) {
    td.setShowDiarized(true);
  }

  // Copy actions for the "Copy for EHR" blocks
  const handleCopyForTebraEhr = async () => {
    if (!ehr.tebraEhrData) return;
    try {
      await navigator.clipboard.writeText(ehr.tebraEhrData.full_formatted_note);
      toast.success('Clinical note copied — paste into Tebra EHR note');
    } catch {
      toast.error('Copy failed');
    }
  };
  const handleCopyTemplatedFullNote = async () => {
    if (!ehr.tebraFullTemplateNote) {
      toast.error('Pick a note template first');
      return;
    }
    try {
      await navigator.clipboard.writeText(ehr.tebraFullTemplateNote);
      toast.success(`${ehr.tebraCurrentTemplate?.name || 'Templated'} note copied — paste into your EHR`);
    } catch {
      toast.error('Copy failed');
    }
  };
  const handleCopySection = async (sectionName, text) => {
    if (!text) { toast.error(`No ${sectionName} content`); return; }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${sectionName} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  if (td.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
      </div>
    );
  }

  const transcript = td.transcript;
  const sessionData = td.sessionData;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8f4 50%, #e3f2fd 100%)' }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-white/50 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              data-testid="back-button"
              className="rounded-xl border-gray-200 hover:bg-gray-50 shrink-0 px-3 sm:px-4"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>

            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 justify-end">
              {!td.editing && (
                <>
                  {transcript?.diarized_text ? (
                    <Button
                      onClick={() => td.setShowDiarized(!td.showDiarized)}
                      variant="outline"
                      data-testid="toggle-diarized-button"
                      className={`rounded-xl border-indigo-200 px-2.5 sm:px-4 text-xs sm:text-sm ${td.showDiarized ? 'bg-indigo-100 text-indigo-700' : 'text-indigo-600 hover:bg-indigo-50'}`}
                    >
                      {td.showDiarized ? <ToggleRight className="w-4 h-4 sm:mr-2" /> : <ToggleLeft className="w-4 h-4 sm:mr-2" />}
                      <span className="hidden sm:inline">Speakers</span>
                    </Button>
                  ) : (
                    <Button
                      onClick={td.handleDiarize}
                      disabled={td.diarizing || transcript?.diarization_status === 'processing'}
                      variant="outline"
                      data-testid="diarize-button"
                      className="rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-2.5 sm:px-4 text-xs sm:text-sm"
                    >
                      {(td.diarizing || transcript?.diarization_status === 'processing')
                        ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
                        : <Users className="w-4 h-4 sm:mr-2" />}
                      <span className="hidden sm:inline">Diarize</span>
                    </Button>
                  )}

                  <Button
                    onClick={notes.handleGenerateDiagnosis}
                    disabled={notes.generatingDiagnosis}
                    variant="outline"
                    data-testid="generate-diagnosis-button"
                    className="rounded-xl border-purple-200 text-purple-600 hover:bg-purple-50 px-2.5 sm:px-4 text-xs sm:text-sm"
                  >
                    {notes.generatingDiagnosis ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" /> : <Brain className="w-4 h-4 sm:mr-2" />}
                    <span className="hidden sm:inline">Diagnosis</span>
                  </Button>

                  <Button
                    onClick={notes.handleGenerateMedicalNote}
                    disabled={notes.generatingNote}
                    variant="outline"
                    data-testid="generate-note-button"
                    className="rounded-xl border-purple-200 text-purple-600 hover:bg-purple-50 px-2.5 sm:px-4 text-xs sm:text-sm"
                  >
                    {notes.generatingNote ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 sm:mr-2" />}
                    <span className="hidden sm:inline">Medical Note</span>
                  </Button>

                  <Button
                    onClick={notes.handleGeneratePsychotherapyNote}
                    disabled={notes.generatingPsychNote}
                    variant="outline"
                    data-testid="generate-psych-note-button"
                    className="rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-2.5 sm:px-4 text-xs sm:text-sm"
                  >
                    {notes.generatingPsychNote ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" /> : <Target className="w-4 h-4 sm:mr-2" />}
                    <span className="hidden sm:inline">Therapy Note</span>
                  </Button>

                  <Button
                    onClick={() => td.setEditing(true)}
                    variant="outline"
                    data-testid="edit-button"
                    className="rounded-xl border-gray-200 hover:bg-gray-50 px-2.5 sm:px-4 text-xs sm:text-sm"
                  >
                    <Edit className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>

                  <Button
                    onClick={() => td.handleDownload('text')}
                    variant="outline"
                    data-testid="download-text-button"
                    className="rounded-xl border-gray-200 hover:bg-gray-50 px-2.5 sm:px-4 text-xs sm:text-sm"
                  >
                    <Download className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">TXT</span>
                  </Button>

                  <Button
                    onClick={() => td.handleDownload('pdf')}
                    data-testid="download-pdf-button"
                    className="rounded-xl bg-gradient-to-r from-purple-500 to-slate-600 hover:from-teal-600 hover:to-cyan-700 px-2.5 sm:px-4 text-xs sm:text-sm"
                  >
                    <Download className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">PDF</span>
                  </Button>

                  <Button
                    onClick={() => td.handleShareSummary({ openMail: true })}
                    disabled={td.sharingSummary}
                    data-testid="share-summary-button"
                    title="Download a single PDF with the transcript, all AI notes, and ICD-10/CPT codes — ideal for emailing a covering psychiatrist or PCP."
                    className="rounded-xl bg-gradient-to-r from-[#C47A5E] to-[#a85d42] hover:from-[#a85d42] hover:to-[#8d4a34] text-white px-2.5 sm:px-4 text-xs sm:text-sm"
                  >
                    {td.sharingSummary ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" /> : <Send className="w-4 h-4 sm:mr-2" />}
                    <span className="hidden sm:inline">{td.sharingSummary ? 'Building…' : 'Share Summary'}</span>
                  </Button>

                  <Button
                    onClick={ehr.handleOpenEhrPush}
                    data-testid="push-to-ehr-button"
                    className="rounded-xl bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white px-2.5 sm:px-4 text-xs sm:text-sm"
                  >
                    <Send className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Push to EHR</span>
                  </Button>
                </>
              )}

              {td.editing && (
                <>
                  <Button
                    onClick={td.cancelEdit}
                    variant="outline"
                    data-testid="cancel-edit-button"
                    className="rounded-xl border-gray-200 hover:bg-gray-50"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>

                  <Button
                    onClick={td.handleSave}
                    disabled={td.saving}
                    data-testid="save-button"
                    className="rounded-xl bg-gradient-to-r from-purple-500 to-slate-600 hover:from-teal-600 hover:to-cyan-700"
                  >
                    {td.saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6">
          <div className={`${notes.showDiagnosisPanel || notes.showNotePanel || notes.showPsychNotePanel ? 'lg:col-span-3' : 'lg:col-span-5'}`}>
            <Card className="p-5 sm:p-8 bg-white/80 backdrop-blur-xl rounded-3xl border border-white/50 shadow-xl">
              <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Session Transcript</h1>
                {sessionData && (sessionData.session_date || sessionData.start_time) && (
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-1" data-testid="session-timing-info">
                    {sessionData.session_date && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                        {sessionData.session_date}
                      </span>
                    )}
                    {sessionData.start_time && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        {sessionData.start_time}
                        {sessionData.end_time && <span>— {sessionData.end_time}</span>}
                        <span className="text-gray-400">{(() => {
                          try {
                            const tz = sessionData.timezone || user?.timezone;
                            if (!tz) return 'EST';
                            const s = new Date().toLocaleTimeString('en-US', { timeZone: tz, timeZoneName: 'short' });
                            const label = s.split(' ').pop();
                            return label && !label.startsWith('AM') && !label.startsWith('PM') ? label : tz;
                          } catch { return 'EST'; }
                        })()}</span>
                      </span>
                    )}
                    {sessionData.duration_seconds > 0 && (
                      <span className="text-gray-500">({Math.floor(sessionData.duration_seconds / 60)}m {sessionData.duration_seconds % 60}s)</span>
                    )}
                  </div>
                )}
                <p className="text-sm text-gray-600">
                  Created: {new Date(transcript.created_at).toLocaleDateString()} at {new Date(transcript.created_at).toLocaleTimeString()}
                </p>
                {transcript.edited_text && (
                  <p className="text-sm text-purple-600 mt-1">
                    Last edited: {new Date(transcript.updated_at).toLocaleDateString()}
                  </p>
                )}
                {transcript.diarization_status === 'processing' && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-indigo-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Identifying speakers...
                  </div>
                )}
                {(sessionData?.notes_generation_status === 'processing' || sessionData?.notes_generation_status === 'pending') && transcript.diarization_status !== 'processing' && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-purple-600" data-testid="notes-auto-generating">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Auto-generating diagnosis, medical notes & therapy notes...
                  </div>
                )}
                {sessionData?.notes_generation_status === 'completed' && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-green-600" data-testid="notes-auto-complete">
                    <Check className="w-3.5 h-3.5" />
                    All notes auto-generated
                  </div>
                )}
                {transcript.diarized_text && td.showDiarized && (
                  <div className="mt-2 flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-purple-200 border-2 border-purple-500 inline-block" /> Provider</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-slate-200 border-2 border-slate-400 inline-block" /> Patient</span>
                  </div>
                )}
              </div>

              {td.editing ? (
                <Textarea
                  value={td.editedText}
                  onChange={(e) => td.setEditedText(e.target.value)}
                  data-testid="transcript-textarea"
                  rows={20}
                  className="w-full font-mono text-sm rounded-2xl border-gray-200 focus:border-purple-500 focus:ring-purple-500"
                />
              ) : td.showDiarized && transcript.diarized_text ? (
                <DiarizedTranscript text={transcript.diarized_text} />
              ) : (
                <div
                  data-testid="transcript-content"
                  className="prose prose-gray max-w-none bg-gray-50 p-4 sm:p-6 rounded-2xl font-mono text-sm leading-relaxed whitespace-pre-wrap"
                >
                  {transcript.edited_text || transcript.text}
                </div>
              )}
            </Card>
          </div>

          {(notes.showDiagnosisPanel || notes.showNotePanel || notes.showPsychNotePanel) && (
            <div className="lg:col-span-2 space-y-6">
              {notes.showDiagnosisPanel && notes.diagnosis && (
                <DiagnosisCard
                  diagnosis={notes.diagnosis}
                  onClose={() => notes.setShowDiagnosisPanel(false)}
                  onCopyCode={copyText}
                />
              )}
              {notes.showNotePanel && notes.medicalNote && (
                <MedicalNoteCard
                  medicalNote={notes.medicalNote}
                  onClose={() => notes.setShowNotePanel(false)}
                  onCopy={copyText}
                  onCopyCode={copyText}
                  onExport={notes.handleExportMedicalNote}
                />
              )}
              {notes.showPsychNotePanel && notes.psychotherapyNote && (
                <PsychotherapyNoteCard
                  note={notes.psychotherapyNote}
                  onClose={() => notes.setShowPsychNotePanel(false)}
                  onCopy={copyText}
                  onCopyCode={copyText}
                  onExport={notes.handleExportPsychotherapyNote}
                />
              )}
            </div>
          )}
        </div>
      </main>

      {/* EHR Push Dialog — kept inline because it densely shares state with the
          surrounding hooks. Refactored into smaller subcomponents would
          require threading 25+ props which adds more friction than it saves. */}
      <Dialog open={ehr.ehrDialogOpen} onOpenChange={ehr.setEhrDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-700 bg-clip-text text-transparent flex items-center gap-2">
              <Send className="w-5 h-5 text-green-600" />
              Push to EHR
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* EHR System Selector */}
            <div data-testid="ehr-system-selector">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-700">Connected EHR:</p>
                {ehr.extensionActive ? (
                  ehr.detectedEHRs.length > 0 ? (
                    <span className="flex items-center gap-1.5 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-medium" data-testid="ehr-live-badge">
                      <Check className="w-3 h-3" /> Live · {ehr.detectedEHRs.length} open
                    </span>
                  ) : (
                    <span className="text-[10px] text-amber-600 font-medium" data-testid="ehr-no-tabs-badge">
                      Extension ready — no EHR tabs open
                    </span>
                  )
                ) : (
                  <span className="text-[10px] text-gray-500 font-medium" data-testid="ehr-no-ext-badge">
                    Extension not detected
                  </span>
                )}
              </div>

              {ehr.detectedEHRs.length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="ehr-detected-cards">
                  {ehr.detectedEHRs.map((e) => {
                    const ehrKey = `extension:${e.ehr_name}`;
                    const selected = ehr.ehrSystem === ehrKey;
                    const health = ehr.ehrHealth[e.ehr_name];
                    const isTebraOnPm = e.ehr_name === 'Tebra' && e.is_chart_page === false;
                    const subtitle = e.ehr_name === 'Tebra'
                      ? (e.is_chart_page === false ? `Tebra PM (Billing) · ${e.ehr_hostname}` : `Tebra EHR (Chart) · ${e.ehr_hostname}`)
                      : `Via Extension · ${e.ehr_hostname}`;
                    return (
                      <button
                        key={e.ehr_name}
                        onClick={() => ehr.setEhrSystem(ehrKey)}
                        data-testid={`ehr-select-${e.ehr_name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex-1 min-w-[160px] p-3 rounded-xl border-2 text-left transition-all ${selected ? 'border-green-500 bg-green-50 ring-2 ring-green-500' : 'border-gray-200 bg-gray-50 hover:border-green-300'}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold text-sm text-gray-900 truncate">{e.ehr_name}</p>
                          <div className="flex items-center gap-1 shrink-0">
                            {e.is_active && (
                              <span className="text-[9px] bg-green-500 text-white font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider">Active</span>
                            )}
                            {isTebraOnPm && (
                              <span className="text-[9px] bg-amber-100 text-amber-700 font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider" title="This Tebra tab is on the PM/billing page. Open a chart/encounter page for note push.">PM</span>
                            )}
                            <HealthBadge health={health} isTebra={false} />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{subtitle}</p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="ehr-no-detection">
                  <ExternalLink className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1 text-xs text-amber-800">
                    <p className="font-semibold">Open your EHR in another browser tab</p>
                    <p className="mt-0.5">It will appear here automatically. Supported: Epic, SimplePractice, Tebra, DrChrono, TherapyNotes, Athenahealth, Cerner, and 9 more.</p>
                  </div>
                </div>
              )}

              {/* Tebra PM billing status */}
              {ehr.ehrSystem === 'extension:Tebra' && (
                <div className={`mt-2 p-3 rounded-xl text-sm ${ehr.tebraStatus?.connected ? 'bg-emerald-50 border border-emerald-200' : ehr.tebraStatus?.error_type === 'network_blocked' ? 'bg-amber-50 border border-amber-200' : 'bg-yellow-50 border border-yellow-200'}`} data-testid="tebra-status">
                  {ehr.tebraStatus?.connected ? (
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="font-semibold text-emerald-800 text-xs">Tebra PM billing API also connected</p>
                        {ehr.tebraStatus.practices?.map((p, i) => (
                          <p key={i} className="text-[11px] text-emerald-700">Practice: {p.name} (ID: {p.id})</p>
                        ))}
                        <p className="text-[11px] text-emerald-600 mt-1">After pushing the chart note above, use "Also push billing to Tebra PM" below to file encounter + CPT/ICD codes.</p>
                      </div>
                    </div>
                  ) : ehr.tebraStatus?.error_type === 'network_blocked' ? (
                    <div>
                      <p className="font-semibold text-amber-800 text-xs">Tebra PM billing API requires whitelisted IP</p>
                      <p className="text-xs text-amber-700 mt-1">Chart note push still works via the extension. Billing push is unavailable until the server IP is whitelisted.</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="text-yellow-800 text-xs">{ehr.tebraStatus?.message || 'Tebra PM billing API not tested'}</p>
                      <Button onClick={ehr.handleTestTebra} disabled={ehr.testingTebra} size="sm" variant="outline" className="rounded-lg text-xs">
                        {ehr.testingTebra ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Test'}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Extension push instructions */}
              {ehr.ehrSystem?.startsWith('extension:') && (() => {
                const ehrName = ehr.ehrSystem.split(':')[1];
                const status = ehr.ehrHealth[ehrName];
                if (status === 'needs-refresh') {
                  return (
                    <div className="mt-2 p-3 rounded-xl text-sm bg-amber-50 border border-amber-200" data-testid="extension-push-info">
                      <p className="font-semibold text-amber-800 text-xs flex items-center gap-1.5">
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh {ehrName} tab first
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        The {ehrName} tab was opened before the extension loaded. Press <strong>Ctrl+R</strong> (or ⌘+R on Mac) on that tab, then push again.
                      </p>
                    </div>
                  );
                }
                if (status === 'error' || status === 'offline') {
                  return (
                    <div className="mt-2 p-3 rounded-xl text-sm bg-red-50 border border-red-200" data-testid="extension-push-info">
                      <p className="font-semibold text-red-800 text-xs flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" /> Extension can't reach the {ehrName} tab
                      </p>
                      <p className="text-xs text-red-700 mt-1">
                        Make sure the tab is open and refresh it, then try again.
                      </p>
                    </div>
                  );
                }
                const chartLabel = ehrName === 'Tebra' ? 'Tebra EHR chart' : `${ehrName} chart`;
                const selectedEhr = ehr.detectedEHRs.find((e) => e.ehr_name === ehrName);
                const tebraOnPm = ehrName === 'Tebra' && selectedEhr?.is_chart_page === false;
                if (tebraOnPm) {
                  return (
                    <div className="mt-2 p-3 rounded-xl text-sm bg-amber-50 border border-amber-200" data-testid="extension-push-info">
                      <p className="font-semibold text-amber-800 text-xs flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" /> Open a Tebra chart page for note push
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        Your detected Tebra tab looks like the <strong>PM/billing</strong> view. Navigate to a patient chart / encounter / SOAP note, then push again. (You can still push billing codes via "Also push billing to Tebra PM" below.)
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="mt-2 p-3 rounded-xl text-sm bg-purple-50 border border-purple-200" data-testid="extension-push-info">
                    <p className="font-semibold text-purple-800 text-xs flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5" /> Real-time push ready
                    </p>
                    <p className="text-xs text-purple-700 mt-1">
                      Click <strong>"Push to {ehrName} now"</strong> below — the note will be injected directly into the open <strong>{chartLabel}</strong> in real-time. We'll switch focus to that tab so you see it land.
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Content Selection */}
            <div data-testid="ehr-push-options">
              <p className="text-sm font-semibold text-gray-700 mb-3">Select what to push:</p>
              <div className="space-y-2">
                {[
                  { key: 'include_medical_note', label: 'Medical Note (SOAP)', icon: '📋' },
                  { key: 'include_psychotherapy_note', label: 'Psychotherapy Note', icon: '🧠' },
                  { key: 'include_billing_codes', label: 'Billing Codes (ICD-10, CPT, E/M, Add-on)', icon: '💳' },
                ].map(({ key, label, icon }) => (
                  <label key={key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors" data-testid={`ehr-option-${key}`}>
                    <input
                      type="checkbox"
                      checked={ehr.ehrOptions[key]}
                      onChange={(e) => ehr.setEhrOptions((prev) => ({ ...prev, [key]: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-800">{icon} {label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Default note template — pick a structure for the push */}
            {ehr.ehrSystem?.startsWith('extension:') && (
              <div data-testid="tebra-preview-panel" className="rounded-2xl border p-4" style={{ background: '#F9F6F0', borderColor: '#E3E8E4' }}>
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold" style={{ color: '#1E3329' }}>
                      {ehr.ehrSystem === 'extension:Tebra' ? 'Tebra EHR chart preview' : 'Note template preview'}
                    </p>
                    <p className="text-[11px]" style={{ color: '#6B8E7B' }}>
                      {ehr.ehrSystem === 'extension:Tebra'
                        ? "Pick the Tebra note template you're using, then review & edit each section. Edits push to the open chart."
                        : `Optional — pick a note-template structure and review & edit each section before pushing to ${ehr.ehrSystem.split(':')[1]}.`}
                    </p>
                  </div>
                  <Button
                    onClick={() => ehr.loadTebraPreview(true)}
                    disabled={ehr.tebraPreviewLoading || !ehr.tebraTemplateKey}
                    variant="outline"
                    size="sm"
                    data-testid="tebra-preview-regenerate"
                    className="text-xs rounded-lg shrink-0"
                    style={{ borderColor: '#2C4A3B', color: '#2C4A3B' }}
                  >
                    {ehr.tebraPreviewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1" />Regenerate</>}
                  </Button>
                </div>

                {/* Template picker */}
                <div className="mb-3">
                  <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#2C4A3B' }}>
                    {ehr.ehrSystem === 'extension:Tebra' ? 'Tebra note template' : 'Default note template'}
                  </label>
                  {(ehr.tebraTemplates || []).length > 6 && (
                    <input
                      type="search"
                      value={ehr.tebraTemplateSearch}
                      onChange={(e) => ehr.setTebraTemplateSearch(e.target.value)}
                      placeholder="Filter templates by name or category…"
                      data-testid="tebra-template-search"
                      className="mt-1 w-full rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-[#2C4A3B]"
                      style={{ border: '1px solid #C5D2CA', color: '#1E3329', background: '#fff' }}
                    />
                  )}
                  {(() => {
                    const term = ehr.tebraTemplateSearch.trim().toLowerCase();
                    const matches = (t) => {
                      if (!term) return true;
                      if (t.key === ehr.tebraTemplateKey) return true;
                      return (
                        (t.name || '').toLowerCase().includes(term) ||
                        (t.category || '').toLowerCase().includes(term)
                      );
                    };
                    const visible = (ehr.tebraTemplates || []).filter(matches);
                    const groupOrder = [];
                    const groups = {};
                    visible.forEach((t) => {
                      const cat = t.category || 'Other';
                      if (!groups[cat]) { groups[cat] = []; groupOrder.push(cat); }
                      groups[cat].push(t);
                    });
                    return (
                      <>
                        <select
                          data-testid="tebra-template-select"
                          value={ehr.tebraTemplateKey}
                          onChange={(e) => {
                            ehr.setTebraTemplateKey(e.target.value);
                            ehr.setTebraPreviewSections(null);
                            ehr.setTebraExpanded({});
                          }}
                          className="mt-1 w-full rounded-lg text-sm p-2.5 focus:outline-none focus:ring-2 focus:ring-[#2C4A3B]"
                          style={{ border: '1px solid #2C4A3B', color: '#1E3329', background: '#fff' }}
                        >
                          <option value="">
                            {ehr.ehrSystem === 'extension:Tebra'
                              ? '— Pick the Tebra template for this push —'
                              : '— Pick a note template (optional) —'}
                          </option>
                          {groupOrder.map((cat) => (
                            <optgroup key={cat} label={cat}>
                              {groups[cat].map((t) => (
                                <option key={t.key} value={t.key}>
                                  {t.is_custom ? '★ ' : ''}{t.name}{ehr.tebraAutoDetected === t.key ? ' · detected in your Tebra tab' : ''}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        {term && visible.length === 0 && (
                          <p className="text-[11px] mt-1 italic" style={{ color: '#C47A5E' }} data-testid="tebra-template-no-match">
                            No templates match "{ehr.tebraTemplateSearch}". Try a different term or create a custom template below.
                          </p>
                        )}
                      </>
                    );
                  })()}
                  <div className="mt-1.5 flex items-center gap-3 text-[11px]">
                    <button
                      type="button"
                      onClick={tpl.openCreate}
                      data-testid="custom-template-create-btn"
                      className="font-semibold inline-flex items-center gap-1 hover:underline"
                      style={{ color: '#2C4A3B' }}
                    >
                      <Plus className="w-3 h-3" /> Create custom template
                    </button>
                    {ehr.tebraCurrentTemplate?.is_custom && (
                      <>
                        <button
                          type="button"
                          onClick={() => tpl.openEdit(ehr.tebraTemplateKey)}
                          data-testid="custom-template-edit-btn"
                          className="font-semibold inline-flex items-center gap-1 hover:underline"
                          style={{ color: '#C47A5E' }}
                        >
                          <Pencil className="w-3 h-3" /> Edit this template
                        </button>
                        <button
                          type="button"
                          onClick={() => tpl.remove(ehr.tebraTemplateKey.replace(/^custom:/, ''))}
                          disabled={tpl.saving}
                          data-testid="custom-template-delete-inline-btn"
                          className="font-semibold inline-flex items-center gap-1 hover:underline disabled:opacity-50"
                          style={{ color: '#B91C1C' }}
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </>
                    )}
                  </div>
                  {ehr.tebraAutoDetected && ehr.tebraAutoDetected !== ehr.tebraTemplateKey && (
                    <button
                      type="button"
                      onClick={() => ehr.setTebraTemplateKey(ehr.tebraAutoDetected)}
                      data-testid="tebra-template-use-detected"
                      className="mt-1.5 text-[11px] font-semibold inline-flex items-center gap-1 hover:underline"
                      style={{ color: '#C47A5E' }}
                    >
                      <Sparkles className="w-3 h-3" />
                      Use detected: {ehr.tebraTemplates?.find((t) => t.key === ehr.tebraAutoDetected)?.name}
                    </button>
                  )}
                  {!ehr.tebraTemplateKey && ehr.ehrSystem === 'extension:Tebra' && (
                    <p className="mt-2 text-[11px]" style={{ color: '#B45309' }}>
                      Pick a template to generate its sections. The extension can auto-detect once you open a Tebra chart.
                    </p>
                  )}
                </div>

                {ehr.tebraPreviewLoading && !ehr.tebraPreviewSections && (
                  <div className="flex items-center gap-2 text-xs py-8 justify-center" style={{ color: '#6B8E7B' }}>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Building the {ehr.tebraCurrentTemplate?.labels?.length || ''} "{ehr.tebraCurrentTemplate?.name || 'Tebra'}" sections…
                  </div>
                )}

                {ehr.tebraPreviewSections && (
                  <div className="space-y-2" data-testid="tebra-section-cards">
                    {ehr.TEBRA_SECTION_ORDER.map((label) => {
                      const value = ehr.tebraPreviewSections[label] || '';
                      const isOpen = !!ehr.tebraExpanded[label];
                      const preview = value.replace(/\s+/g, ' ').slice(0, 80);
                      const charCount = value.length;
                      return (
                        <div
                          key={label}
                          className="rounded-xl border overflow-hidden transition-shadow"
                          style={{ borderColor: isOpen ? '#2C4A3B' : '#E3E8E4', background: '#fff' }}
                          data-testid={`tebra-section-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                          <button
                            type="button"
                            onClick={() => ehr.setTebraExpanded((prev) => ({ ...prev, [label]: !prev[label] }))}
                            className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#F9F6F0] transition-colors"
                          >
                            <div
                              className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold"
                              style={{ background: value ? '#2C4A3B' : '#E3E8E4', color: value ? '#F9F6F0' : '#6B8E7B' }}
                            >
                              {value ? <Check className="w-3.5 h-3.5" /> : '·'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold truncate" style={{ color: '#1C1917' }}>{label}</p>
                              {!isOpen && (
                                <p className="text-xs truncate mt-0.5" style={{ color: '#6B8E7B' }}>
                                  {preview || <em>empty</em>}
                                  {value.length > 80 ? '…' : ''}
                                </p>
                              )}
                            </div>
                            <span className="text-[10px] font-mono tabular-nums shrink-0" style={{ color: '#6B8E7B' }}>
                              {charCount}c
                            </span>
                            {isOpen ? <ChevronUp className="w-4 h-4 shrink-0" style={{ color: '#6B8E7B' }} /> : <ChevronDown className="w-4 h-4 shrink-0" style={{ color: '#6B8E7B' }} />}
                          </button>
                          {isOpen && (
                            <div className="px-3 pb-3 pt-0 border-t" style={{ borderColor: '#E3E8E4' }}>
                              <textarea
                                value={value}
                                onChange={(e) => ehr.setTebraPreviewSections((prev) => ({ ...(prev || {}), [label]: e.target.value }))}
                                rows={Math.min(10, Math.max(3, value.split('\n').length))}
                                className="w-full mt-2 rounded-lg text-xs p-2.5 font-mono leading-relaxed focus:outline-none focus:ring-2 resize-y"
                                style={{ border: '1px solid #E3E8E4', color: '#1C1917', background: '#F9F6F0' }}
                                data-testid={`tebra-section-edit-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                placeholder={`Enter ${label} content…`}
                              />
                              <div className="flex items-center justify-between mt-2">
                                <p className="text-[10px]" style={{ color: '#6B8E7B' }}>
                                  Edits will push to the open Tebra chart for this field.
                                </p>
                                {value && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard?.writeText(value).then(() => toast.success(`Copied ${label}`));
                                    }}
                                    className="text-[10px] font-semibold inline-flex items-center gap-1 hover:underline"
                                    style={{ color: '#C47A5E' }}
                                  >
                                    <Copy className="w-3 h-3" /> Copy
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div className="flex items-center justify-between pt-1">
                      <p className="text-[11px]" style={{ color: '#6B8E7B' }}>
                        {Object.values(ehr.tebraPreviewSections).filter((v) => v && v.trim()).length} / {ehr.TEBRA_SECTION_ORDER.length} sections with content
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => ehr.setTebraExpanded(Object.fromEntries(ehr.TEBRA_SECTION_ORDER.map((l) => [l, true])))}
                          className="text-[10px] font-semibold hover:underline"
                          style={{ color: '#2C4A3B' }}
                        >
                          Expand all
                        </button>
                        <span style={{ color: '#6B8E7B' }}>·</span>
                        <button
                          type="button"
                          onClick={() => ehr.setTebraExpanded({})}
                          className="text-[10px] font-semibold hover:underline"
                          style={{ color: '#2C4A3B' }}
                        >
                          Collapse all
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Push Preview */}
            {ehr.ehrPreview && (ehr.ehrPreview.template || (ehr.ehrPreview.sections?.length ?? 0) > 0) && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4" data-testid="ehr-push-preview">
                <p className="text-xs font-semibold text-green-800 uppercase mb-2">Push Preview</p>
                <p className="text-xs text-green-700 mb-2" data-testid="ehr-push-preview-meta">
                  Patient: {ehr.ehrPreview.patient?.name || '—'}
                  {ehr.ehrPreview.session?.date ? ` | Date: ${ehr.ehrPreview.session.date}` : ''}
                </p>
                <div className="space-y-1">
                  {ehr.ehrPreview.template && (
                    <div className="flex items-center gap-2 text-xs text-green-900 font-semibold" data-testid="ehr-push-preview-template">
                      <Sparkles className="w-3 h-3" />
                      Template: {ehr.ehrPreview.template.name} · {ehr.ehrPreview.template.section_count} sections
                    </div>
                  )}
                  {(ehr.ehrPreview.sections || []).map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-green-800" data-testid={`ehr-push-preview-section-${s.type}`}>
                      <Check className="w-3 h-3" />
                      {s.title}
                    </div>
                  ))}
                  {ehr.ehrPreview.template && (ehr.ehrPreview.sections?.length ?? 0) === 0 && (
                    <p className="text-[11px] text-green-700/80 italic mt-1">
                      Pushing the templated note only.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2">
              <Button
                onClick={ehr.handleEhrPush}
                disabled={ehr.ehrPushing || !ehr.ehrSystem || (!Object.values(ehr.ehrOptions).some((v) => v) && !ehr.tebraTemplateKey)}
                data-testid="ehr-push-submit"
                className="w-full rounded-xl bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white disabled:opacity-50"
              >
                {ehr.ehrPushing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Pushing...</>
                ) : ehr.ehrSystem?.startsWith('extension:') ? (
                  <><Send className="w-4 h-4 mr-2" />
                    {ehr.ehrSystem === 'extension:Tebra'
                      ? 'Push note to Tebra EHR chart now'
                      : `Push to ${ehr.ehrSystem.split(':')[1]} now`}
                  </>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Select a Connected EHR</>
                )}
              </Button>

              {ehr.ehrSystem === 'extension:Tebra' && ehr.tebraStatus?.connected && (
                <Button
                  onClick={ehr.handlePushTebraBilling}
                  disabled={ehr.ehrPushing || !ehr.ehrOptions.include_billing_codes}
                  variant="outline"
                  data-testid="ehr-push-tebra-billing"
                  className="w-full rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs"
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  Also push billing (CPT/ICD) to Tebra PM
                </Button>
              )}
            </div>

            {/* Universal EHR Clinical Notes — copy block */}
            {ehr.tebraEhrData && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 space-y-3" data-testid="universal-ehr-copy-section">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-indigo-800">Copy for EHR (Clinical Notes)</p>
                  <Button
                    onClick={handleCopyForTebraEhr}
                    size="sm"
                    data-testid="copy-full-tebra-ehr"
                    className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy Full Note
                  </Button>
                </div>
                <p className="text-xs text-indigo-600">Copy the full note or individual sections to paste into any EHR system</p>
                <div className="space-y-2">
                  {[
                    { key: 'subjective', label: 'Subjective / Chief Complaint', color: 'bg-purple-50 border-purple-200 text-purple-800' },
                    { key: 'objective', label: 'Objective / Observations', color: 'bg-blue-50 border-blue-200 text-blue-800' },
                    { key: 'assessment', label: 'Assessment / Diagnosis', color: 'bg-amber-50 border-amber-200 text-amber-800' },
                    { key: 'plan', label: 'Plan', color: 'bg-green-50 border-green-200 text-green-800' },
                    { key: 'psychotherapy_note', label: 'Psychotherapy Note', color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
                    { key: 'risk_assessment', label: 'Risk Assessment', color: 'bg-red-50 border-red-200 text-red-800' },
                    { key: 'billing', label: 'Billing Codes', color: 'bg-slate-50 border-slate-200 text-slate-800' },
                  ].filter((s) => ehr.tebraEhrData.sections?.[s.key]).map(({ key, label, color }) => (
                    <div key={key} className={`flex items-start justify-between rounded-xl border p-3 ${color}`} data-testid={`tebra-section-${key}`}>
                      <div className="flex-1 min-w-0 mr-2">
                        <p className="text-xs font-semibold">{label}</p>
                        <p className="text-xs opacity-75 mt-0.5 line-clamp-2">{ehr.tebraEhrData.sections[key].substring(0, 120)}...</p>
                      </div>
                      <Button
                        onClick={() => handleCopySection(label, ehr.tebraEhrData.sections[key])}
                        size="sm"
                        variant="outline"
                        data-testid={`copy-section-${key}`}
                        className="shrink-0 rounded-lg text-xs h-7 px-2"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Template-specific Clinical Notes — second copy block */}
            {ehr.tebraCurrentTemplate && ehr.tebraPreviewSections && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 space-y-3" data-testid="templated-ehr-copy-section">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-emerald-800 truncate">
                      Copy for EHR — {ehr.tebraCurrentTemplate.name}
                    </p>
                    <p className="text-xs text-emerald-700/80 mt-0.5">
                      Comprehensive note generated from your chosen template — all {(ehr.tebraCurrentTemplate.labels || []).length} sections included
                    </p>
                  </div>
                  <Button
                    onClick={handleCopyTemplatedFullNote}
                    size="sm"
                    disabled={ehr.tebraPreviewLoading || !ehr.tebraFullTemplateNote}
                    data-testid="copy-full-templated-ehr"
                    className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs shrink-0"
                  >
                    {ehr.tebraPreviewLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Copy className="w-3 h-3 mr-1" />}
                    Copy Full Note
                  </Button>
                </div>

                <div className="space-y-2">
                  {(ehr.tebraCurrentTemplate.labels || [])
                    .filter((label) => (ehr.tebraPreviewSections[label] || '').trim())
                    .map((label) => {
                      const text = ehr.tebraPreviewSections[label];
                      const safeId = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                      return (
                        <div
                          key={label}
                          className="flex items-start justify-between rounded-xl border bg-white border-emerald-200 p-3"
                          data-testid={`templated-section-${safeId}`}
                        >
                          <div className="flex-1 min-w-0 mr-2">
                            <p className="text-xs font-semibold text-emerald-900">{label}</p>
                            <p className="text-xs text-emerald-800/70 mt-0.5 line-clamp-2">
                              {text.substring(0, 140)}{text.length > 140 ? '…' : ''}
                            </p>
                          </div>
                          <Button
                            onClick={() => handleCopySection(label, text)}
                            size="sm"
                            variant="outline"
                            data-testid={`copy-templated-section-${safeId}`}
                            className="shrink-0 rounded-lg text-xs h-7 px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })}
                  {(ehr.tebraCurrentTemplate.labels || []).every((l) => !(ehr.tebraPreviewSections[l] || '').trim()) && !ehr.tebraPreviewLoading && (
                    <p className="text-xs text-emerald-700/70 italic">No content generated yet for this template.</p>
                  )}
                </div>
              </div>
            )}

            {/* Hint when no template chosen */}
            {!ehr.tebraCurrentTemplate && ehr.tebraEhrData && (
              <div className="rounded-2xl border border-dashed border-emerald-300 bg-emerald-50/40 p-3 flex items-start gap-2" data-testid="templated-ehr-hint">
                <Sparkles className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div className="text-xs text-emerald-800">
                  <span className="font-semibold">Tip:</span> Pick a note template in the EHR Push dialog to also generate a second, template-specific clinical note (with all template sections) that you can copy in one click.
                </div>
              </div>
            )}

            {/* Push Result */}
            {ehr.ehrPushResult && (
              <div className={`rounded-xl p-4 ${ehr.ehrPushResult.status === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="ehr-push-result">
                <p className={`text-sm font-semibold ${ehr.ehrPushResult.status === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                  {ehr.ehrPushResult.status === 'success' ? <Check className="w-4 h-4 inline mr-1" /> : null}
                  {ehr.ehrPushResult.message}
                </p>
                {ehr.ehrPushResult.sections_pushed && (
                  <div className="mt-2 space-y-1">
                    {ehr.ehrPushResult.sections_pushed.map((s, i) => (
                      <p key={i} className="text-xs text-green-700 flex items-center gap-1"><Check className="w-3 h-3" />{s}</p>
                    ))}
                  </div>
                )}
                {ehr.ehrPushResult.tebra_details?.encounter_id && (
                  <div className="mt-2 pt-2 border-t border-green-200">
                    <p className="text-xs font-semibold text-green-800">Tebra Encounter Details:</p>
                    <p className="text-xs text-green-700">Encounter ID: {ehr.ehrPushResult.tebra_details.encounter_id}</p>
                    <p className="text-xs text-green-700">Patient ID: {ehr.ehrPushResult.tebra_details.patient_id}</p>
                    <p className="text-xs text-green-700">Provider: {ehr.ehrPushResult.tebra_details.provider}</p>
                    {ehr.ehrPushResult.tebra_details.cpt_codes_pushed && (
                      <p className="text-xs text-green-700">CPT: {ehr.ehrPushResult.tebra_details.cpt_codes_pushed.join(', ')}</p>
                    )}
                    {ehr.ehrPushResult.tebra_details.diagnosis_codes && (
                      <p className="text-xs text-green-700">ICD-10: {ehr.ehrPushResult.tebra_details.diagnosis_codes.join(', ')}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <EhrPushHistory history={ehr.ehrPushHistory} />
          </div>
        </DialogContent>
      </Dialog>

      <CustomTemplateModal
        open={tpl.modalOpen}
        onOpenChange={tpl.setModalOpen}
        editingId={tpl.editingId}
        name={tpl.name}
        setName={tpl.setName}
        labelsText={tpl.labelsText}
        setLabelsText={tpl.setLabelsText}
        guidelines={tpl.guidelines}
        setGuidelines={tpl.setGuidelines}
        saving={tpl.saving}
        onSave={tpl.save}
        onDelete={() => tpl.remove()}
      />
    </div>
  );
}
